/**
 * Configurações › Integrações › E-mail (mig 786; D36). Os e-mails que saem sozinhos ao cliente, cada um com a sua chave.
 * 🔴 Todos nascem desligados. Ligar alcança cliente, então a tela PERGUNTA antes, com a consequência escrita; desligar
 * não pergunta (parar nunca é o gesto perigoso).
 * O editor (mig 791): assunto, abertura e cópia fixa, com prévia e teste, abre no lugar em "Editar texto".
 */
import { useEffect, useState } from 'react';

import { Badge, Button, HelpTip } from '@/components/ui';
import { useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useToast } from '@/components/ui/Toast';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format-date';

import { mensagemDoErro } from '@/pages/os-sistema/os-api';

import { EditorDoModeloDeEmail, type ModeloEditavel } from './EditorDoModeloDeEmail';

interface ModeloDeEmail extends ModeloEditavel {
  chave: string;
  nome: string;
  quando: string;
  para: string;
  ligado: boolean;
  /** false = sai sempre que o fato acontece (os do orçamento e os do Portal): sem botão de ligar, só o texto se edita. */
  temInterruptor?: boolean;
  mudadoPor: string | null;
  mudadoEm: string | null;
}

const B = '/api/v1/admin/email/modelos';
const dataHora = (iso: string) => formatDateTime(iso);

export function PaginaEmail() {
  const [modelos, setModelos] = useState<ModeloDeEmail[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [mudando, setMudando] = useState<string | null>(null);
  const [editando, setEditando] = useState<string | null>(null);
  const confirmar = useConfirmDialog();
  const { show } = useToast();

  useEffect(() => {
    api
      .get<{ modelos: ModeloDeEmail[] }>(B)
      .then((r) => setModelos(r.modelos))
      .catch((e) => setErro(mensagemDoErro(e, 'Não deu para abrir os modelos de e-mail.')));
  }, []);

  async function alternar(m: ModeloDeEmail) {
    if (!m.ligado) {
      const ok = await confirmar({
        title: `Ligar "${m.nome}"?`,
        message: `A partir de agora este e-mail sai sozinho. ${m.quando} Quem recebe: ${m.para}`,
        confirmLabel: 'Ligar',
      });
      if (!ok) return;
    }
    setErro(null);
    setMudando(m.chave);
    try {
      const r = await api.patch<{ modelos: ModeloDeEmail[] }>(`${B}/${encodeURIComponent(m.chave)}`, { ligado: !m.ligado });
      setModelos(r.modelos);
      show(m.ligado ? `"${m.nome}" desligado.` : `"${m.nome}" ligado.`, { variant: 'success' });
    } catch (e) {
      setErro(mensagemDoErro(e));
    } finally {
      setMudando(null);
    }
  }

  if (!modelos) return erro ? <p role="alert" className="text-sm text-red-deep">{erro}</p> : <p className="text-sm text-ink-3">Abrindo…</p>;

  return (
    <div className="flex flex-col gap-4" data-testid="pagina-email">
      <p className="flex items-center gap-1 text-sm text-ink-2 dark:text-ink-dark-2">
        Os novos nascem desligados. Desligado, nada sai: a trilha anota o que teria saído. Os do orçamento e os do Portal saem sempre.
        <HelpTip texto="Leia na trilha o que teria saído antes de ligar. O freio dos e-mails, em WhatsApp, continua valendo por cima destas chaves." />
      </p>
      {erro ? (
        <p role="alert" className="text-sm font-semibold text-red-deep dark:text-red-glow">
          {erro}
        </p>
      ) : null}
      <ul className="flex flex-col gap-3">
        {modelos.map((m) => (
          <li key={m.chave} className="rounded-lg border border-line bg-paper-surface p-4 dark:border-line-dark dark:bg-paper-dark-surface">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 basis-full sm:basis-0 sm:flex-1">
                <h3 className="flex items-center gap-2 text-sm font-bold">
                  {m.nome}
                  {m.temInterruptor === false ? (
                    <Badge variant="info">Sai sempre</Badge>
                  ) : (
                    <Badge variant={m.ligado ? 'success' : 'neutral'}>{m.ligado ? 'Ligado' : 'Desligado'}</Badge>
                  )}
                </h3>
                <p className="mt-1 text-sm text-ink-2 dark:text-ink-dark-2">{m.quando}</p>
                <p className="mt-1 text-sm text-ink-3 dark:text-ink-dark-3">Quem recebe: {m.para}</p>
                {m.texto.personalizado && m.texto.textoMudadoEm ? (
                  <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
                    Texto mudado por {m.texto.textoMudadoPor ?? 'alguém'} em {dataHora(m.texto.textoMudadoEm)}.
                  </p>
                ) : null}
                {m.mudadoEm ? (
                  <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
                    {m.ligado ? 'Ligado' : 'Desligado'} por {m.mudadoPor ?? 'alguém'} em {dataHora(m.mudadoEm)}.
                  </p>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  aria-expanded={editando === m.chave}
                  onClick={() => setEditando((e) => (e === m.chave ? null : m.chave))}
                  className="min-h-[44px] md:min-h-0"
                >
                  {editando === m.chave ? 'Fechar o texto' : 'Editar texto'}
                </Button>
                {m.temInterruptor === false ? null : (
                  <Button size="sm" variant={m.ligado ? 'secondary' : 'primary'} disabled={mudando === m.chave} onClick={() => void alternar(m)} className="min-h-[44px] md:min-h-0">
                    {m.ligado ? 'Desligar' : 'Ligar'}
                  </Button>
                )}
              </div>
            </div>
            {editando === m.chave ? <EditorDoModeloDeEmail modelo={m} aoSalvar={setModelos} /> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
